import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { openFile, isPdfFile } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, Download, FileText, Loader2, Image } from "lucide-react";

export default function CustomerFolder() {
  const { catalogId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [records, setRecords] = useState(null);
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/companies/${user.company_id}/folders/${catalogId}/records`).then((r) => setRecords(r.data));
    api.get(`/companies/${user.company_id}/folders`).then((r) => {
      const f = r.data.find((x) => String(x.index) === String(catalogId));
      setTitle(f?.title || "Klasör");
    });
  }, [user, catalogId]);

  if (!records) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;

  return (
    <div className="fade-in space-y-6">
      <button onClick={() => nav("/musteri")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-customer-dashboard">
        <ArrowLeft className="h-4 w-4" /> Belgelerim
      </button>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600 text-white"><FileText className="h-6 w-6" /></div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#0a192f]">{title}</h1>
      </div>

      {records.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">Bu klasörde yayımlanmış belge yok.</Card>
      ) : (
        <div className="space-y-3">
          {records.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`customer-record-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0a192f]">{r.name}</span>
                    {r.subtype && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{r.subtype}</span>}
                  </div>
                  {r.description && <p className="text-sm text-slate-500">{r.description}</p>}
                  {r.current && <p className="mt-1 text-xs text-slate-400">{r.current.filename}{r.current.deadline ? ` · Son tarih: ${r.current.deadline}` : ""}</p>}
                  {r.status && <div className="mt-2"><StatusBadge status={r.status} /></div>}
                  {r.photos && r.photos.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.photos.map((p) => (
                        <button key={p.id} onClick={() => openFile(`/photos/${p.id}/download`)} className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
                          <Image className="mr-1 inline h-3 w-3" />{p.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {r.current && (
                  <div className="flex gap-2">
                    {isPdfFile(r.current.filename) && <Button variant="outline" size="sm" onClick={() => openFile(`/files/${r.current.version_id}/download`)} data-testid={`pdf-view-button-${r.id}`}>
                      <Eye className="mr-1 h-4 w-4" /> Görüntüle
                    </Button>}
                    <Button className="bg-emerald-600 hover:bg-emerald-700" size="sm" onClick={() => openFile(`/files/${r.current.version_id}/download`, true, r.current.filename)} data-testid={`pdf-download-button-${r.id}`}>
                      <Download className="mr-1 h-4 w-4" /> İndir
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
